import {
  call,
  CallEffect,
  put,
  PutEffect,
  takeLatest,
} from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import {
  ExportAdminLogsParams,
  GetAdminLogCategoriesResponse,
  GetAdminLogRequestParams,
  GetAdminLogsResponseWithPagination,
} from 'features/admin-logs/api/types';
import { PayloadAction } from '@reduxjs/toolkit';
import { getArrayApiErrors } from 'utils/get-array-api-errors';
import { toast } from 'react-toastify';
import { extractErrorMessage } from 'utils/extract-error-message.util';
import { adminLogsActions } from 'features/admin-logs/redux/admin-logs.actions';
import {
  exportAdminLogsAPI,
  getAdminLogCategories,
  getAdminLogs,
} from 'features/admin-logs/api';

function* getAdminLogsList({
  payload,
}: PayloadAction<GetAdminLogRequestParams>): Generator<
  CallEffect<AxiosResponse<GetAdminLogsResponseWithPagination>> | PutEffect,
  void,
  AxiosResponse<GetAdminLogsResponseWithPagination>
> {
  try {
    const response = yield call(getAdminLogs, payload);
    yield put(adminLogsActions.getAdminLogsListSuccess(response.data));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(
      adminLogsActions.getAdminLogsListFailure(getArrayApiErrors(error))
    );
  }
}

function* getAdminLogCategoriesList(): Generator<
  CallEffect<AxiosResponse<GetAdminLogCategoriesResponse>> | PutEffect,
  void,
  AxiosResponse<GetAdminLogCategoriesResponse>
> {
  try {
    const response = yield call(getAdminLogCategories);
    yield put(adminLogsActions.getAdminLogCategoriesSuccess(response.data));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(
      adminLogsActions.getAdminLogCategoriesFailure(getArrayApiErrors(error))
    );
  }
}

function* exportAdminLogs({
  payload,
}: PayloadAction<ExportAdminLogsParams>): Generator<
  CallEffect<AxiosResponse<Blob>> | PutEffect,
  void,
  AxiosResponse<Blob>
> {
  try {
    const response = yield call(exportAdminLogsAPI, payload);

    const href = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    const fileName = `admin-logs-report-${new Date().toISOString()}.${
      payload.format
    }`;
    link.href = href;
    link.setAttribute('download', fileName);
    link.click();
    URL.revokeObjectURL(href);
    yield put(adminLogsActions.exportAdminLogsSuccess());
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(
      adminLogsActions.exportAdminLogsFailure(getArrayApiErrors(error))
    );
  }
}

function* getAdminLogsSaga() {
  yield takeLatest(adminLogsActions.getAdminLogsListRequest, getAdminLogsList);
  yield takeLatest(
    adminLogsActions.getAdminLogCategoriesRequest,
    getAdminLogCategoriesList
  );
  yield takeLatest(adminLogsActions.exportAdminLogsRequest, exportAdminLogs);
}

export default getAdminLogsSaga;
